import PropTypes from "prop-types"
import React, { forwardRef, useImperativeHandle, useMemo, useRef, useState } from "react"
import { Badge } from "reactstrap"
import { Box } from "@mui/material"
import { Link } from "react-router-dom"
import { withTranslation } from "react-i18next"
import moment from "moment"
import { apiBuscarDispositivos } from "../../helpers/backend_helper"
import ListDataTable from "../../components/Common/list-data-table"
import { baseTableProps } from "../../components/Common/table-theme"

const describeDateTime = (dateStr, t) => dateStr
  ? moment(dateStr).format("DD/MM/YY HH:mm")
  : t("bateria.nd")

const describeBatteryColor = percentual => {
  if (percentual == null) return "secondary"
  if (percentual > 50) return "success"
  if (percentual >= 20) return "warning"
  return "danger"
}

const describeApp = (app, t) => {
  if (!app || app === "SelfMenu") return t("dispositivo.app.selfmenu")
  if (app === "FASTFOOD_MOBILE") return t("dispositivo.app.fastfood_mobile")
  return app
}

/* Combina com o mesmo valor no business do backend. */
const LICENCA_LEGADO = "LEGADO"

/* Cabeçalho sempre em uma linha: a coluna é redimensionável se o texto não couber. */
const CABECALHO_SEM_QUEBRA = {
  sx: {
    ...baseTableProps.muiTableHeadCellProps.sx,
    whiteSpace: "nowrap",
    "& .Mui-TableHeadCell-Content-Wrapper": {
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis"
    }
  }
}

const describeAdquirente = (adquirente, t) => {
  if (!adquirente) return null
  const key = `dispositivo.adquirente.${adquirente}`
  const translated = t(key)
  return translated === key ? adquirente : translated
}

const describeLicencaAtiva = (val, t) => {
  if (val === "S") return { label: t("dispositivo.licenca_ativa.ativo"),   color: "success" }
  if (val === "N") return { label: t("dispositivo.licenca_ativa.inativo"),  color: "danger"  }
  return                       { label: t("dispositivo.licenca_ativa.legado"), color: "secondary" }
}

const DispositivosTable = forwardRef((props, refFather) => {
  const ref = useRef()
  const { t, filters, usePermissionEdit, usePermissionDelete,
    onVisualizarLogs, onEditar, onExcluir } = props

  const [data, setData] = useState([])

  useImperativeHandle(refFather, () => ({
    search() { ref?.current?.search() }
  }))

  const colunas = useMemo(() => [
    { header: t("dispositivo.registro"),
      accessorKey: "id", size: 90,
      muiTableBodyCellProps: { align: "right" } },
    /* Aplicativo e Adquirente já têm filtro próprio no topo da tela. */
    { header: t("dispositivo.app"),
      accessorKey: "app", size: 140, enableColumnFilter: false,
      Cell: ({ cell }) => describeApp(cell.getValue(), t) },
    { header: t("dispositivo.empresa"),
      accessorKey: "empresa_RazaoSocial", size: 160, filterFn: "contains" },
    // O filtro do topo exibe a razão social e pesquisa também pela Fantasia; esta coluna correlaciona as duas.
    { header: t("dispositivo.empresa.fantasia"),
      accessorKey: "empresa_Fantasia", size: 180, filterFn: "contains" },
    { header: t("dispositivo.usuarionome"),
      accessorKey: "usuario_NomeCompleto", size: 180, filterFn: "contains" },
    { header: t("dispositivo.usuarioemail"),
      accessorKey: "usuario_email", size: 180, filterFn: "contains" },
    { header: t("dispositivo.imei"),
      accessorKey: "imei", size: 180, filterFn: "contains" },
    // Sem filterFn a condição cai em igualdade exata, e ninguém digita a versão inteira.
    { header: t("dispositivo.versaoandroid"),
      accessorKey: "versao_android", size: 130, filterFn: "contains" },
    { header: t("dispositivo.versaoapp"),
      accessorKey: "versao_app", size: 110, filterFn: "contains" },
    { header: t("dispositivo.numeromesa"),
      accessorKey: "numero_mesa", size: 90 },
    { header: t("dispositivo.caixa"),
      accessorKey: "caixa", size: 90 },
    { header: t("dispositivo.adquirente"),
      accessorKey: "adquirente", size: 120, enableColumnFilter: false,
      Cell: ({ cell }) => describeAdquirente(cell.getValue(), t) },
    { header: t("dispositivo.adquirente_codigo"),
      accessorKey: "adquirente_codigo", size: 160, filterFn: "contains" },
    { header: t("dispositivo.licenca_ativa"),
      accessorKey: "licenca_ativa", size: 130,
      filterFn: "equals", filterVariant: "select",
      filterSelectOptions: [
        { text: t("dispositivo.licenca_ativa.ativo"), value: "S" },
        { text: t("dispositivo.licenca_ativa.inativo"), value: "N" },
        // Legado não é valor da coluna: identifica o registro anterior ao controle de licença.
        { text: t("dispositivo.licenca_ativa.legado"), value: LICENCA_LEGADO }
      ],
      Cell: ({ cell }) => {
        const { label, color } = describeLicencaAtiva(cell.getValue(), t)
        return (
          <Badge className={ `font-size-12 badge-soft-${color}` } color={ color } pill>
            { label }
          </Badge>
        )
      } },
    { header: t("dispositivo.licenca_data_limite"),
      accessorKey: "licenca_data_limite", size: 160,
      Cell: ({ cell }) => describeDateTime(cell.getValue(), t),
      sortingFn: "datetime", enableColumnFilter: false },
    { header: t("dispositivo.bateria"),
      accessorKey: "percentual_bateria", size: 100,
      enableColumnFilter: false,
      Cell: ({ cell }) => {
        const pct = cell?.getValue()
        const color = describeBatteryColor(pct)
        return (
          <Badge className={ `font-size-12 badge-soft-${color}` } color={ color } pill>
            { pct != null ? `${pct}%` : t("dispositivo.bateria.nd") }
          </Badge>
        )
      } },
    { header: t("dispositivo.datainclusao"),
      accessorKey: "created_at", size: 150,
      Cell: ({ cell }) => describeDateTime(cell?.getValue(), t),
      sortingFn: "datetime", enableColumnFilter: false },
    { header: t("dispositivo.dataultatualizacao"),
      accessorKey: "updated_at", size: 150,
      Cell: ({ cell }) => describeDateTime(cell?.getValue(), t),
      sortingFn: "datetime", enableColumnFilter: false },
    { header: t("dispositivo.Altfunc"),
      accessorKey: "Altfunc_NomeCompleto", size: 180, filterFn: "contains" },
  ], [t])

  const columnVisibility = {
    imei: false,
    numero_mesa: false,
    caixa: false,
  }

  return (
    <ListDataTable
      ref={ ref }
      searchFn={ params => apiBuscarDispositivos(params) }
      advancedFilters={ filters }
      columns={ colunas }
      data={ data }
      setData={ setData }
      columnVisibility={ columnVisibility }
      muiTableHeadCellProps={ CABECALHO_SEM_QUEBRA }
      enableRowActions={ true }
      defaultSorting={ [{ id: "id", desc: true }] }
      renderRowActions={ row => (
        <Box sx={ { display: "flex", gap: "0.5rem" } }>
          <Link to="#" className="text-primary"
                title={ t("cardapio.dispositivo.lista.acao.visualizarlogs") }>
            <i className="mdi mdi-eye font-size-18 ms-1"
               onClick={ () => onVisualizarLogs(row.original) } />
          </Link>
          { usePermissionEdit && (
            <Link to="#" className="text-primary"
                  title={ t("cardapio.dispositivo.lista.acao.editar") }>
              <i className="mdi mdi-pencil font-size-18 ms-1"
                 onClick={ () => onEditar(row.original) } />
            </Link>
          ) }
          { usePermissionDelete && (
            <Link to="#" className="text-danger"
                  title={ t("cardapio.dispositivo.lista.acao.excluir") }>
              <i className="mdi mdi-trash-can font-size-18 ms-1"
                 onClick={ () => onExcluir(row.original) } />
            </Link>
          ) }
        </Box>
      ) }
    />
  )
})

DispositivosTable.propTypes = {
  t: PropTypes.func,
  filters: PropTypes.array,
  user: PropTypes.object,
  usePermissionEdit: PropTypes.bool,
  usePermissionDelete: PropTypes.bool,
  onVisualizarLogs: PropTypes.func.isRequired,
  onEditar: PropTypes.func.isRequired,
  onExcluir: PropTypes.func.isRequired
}

export default withTranslation()(DispositivosTable)
